"use client";

import * as React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  FileText,
  AlertCircle,
  Play,
  Volume2,
  Presentation,
  RectangleHorizontal,
  Table,
  Image,
  Link,
  Globe,
  File,
} from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StudyNotUploadedDocument, StudyDocument } from "../../types";
import { formatFileSize } from "../../hooks/useUserLimits";
import { cn } from "@/lib/utils";
import { getSourceIcon } from "./course-source-list";
import { getFileSizeNumeric } from "./sources/source-viewer";

type FileTypeConfig = {
  extension: string;
  bgColor: string;
  textColor: string;
  icon?: any;
};

const getFileTypeConfig = (mime: string): FileTypeConfig => {
  const lower = mime.toLowerCase();

  // --- Documents ---
  if (lower.includes("pdf")) {
    return {
      extension: "PDF",
      bgColor: "bg-red-500",
      textColor: "text-white",
    };
  }

  if (
    lower.includes("powerpoint") ||
    lower.includes("presentation") ||
    lower.includes("ppt")
  ) {
    return {
      extension: "PPT",
      bgColor: "bg-amber-400",
      textColor: "text-white",
      icon: Presentation,
    };
  }

  if (lower.includes("word") || lower.includes("doc")) {
    return {
      extension: "DOC",
      bgColor: "bg-blue-500",
      textColor: "text-white",
      icon: FileText,
    };
  }

  if (
    lower.includes("excel") ||
    lower.includes("spreadsheet") ||
    lower.includes("xls") ||
    lower.includes("csv")
  ) {
    return {
      extension: "XLS",
      bgColor: "bg-green-600",
      textColor: "text-white",
      icon: Table,
    };
  }

  if (lower.includes("url")) {
    return {
      extension: "URL",
      bgColor: "bg-indigo-500",
      textColor: "text-white",
      icon: Link,
    };
  }

  // --- Web / links ---
  if (
    lower.includes("text/html") ||
    lower.includes("website") ||
    lower.includes("http")
  ) {
    return {
      extension: "WEB",
      bgColor: "bg-indigo-500",
      textColor: "text-white",
      icon: Globe,
    };
  }

  // --- Video ---
  if (
    lower.startsWith("video/") ||
    lower.includes("mp4") ||
    lower.includes("webm")
  ) {
    return {
      extension: "MP4",
      bgColor: "bg-pink-500",
      textColor: "text-white",
      icon: Play,
    };
  }

  // --- Audio ---
  if (
    lower.startsWith("audio/") ||
    lower.includes("mp3") ||
    lower.includes("wav")
  ) {
    return {
      extension: "MP3",
      bgColor: "bg-emerald-500",
      textColor: "text-white",
      icon: Volume2,
    };
  }

  if (
    lower.includes("text") ||
    lower.includes("markdown") ||
    lower.includes("md")
  ) {
    return {
      extension: "TXT",
      bgColor: "bg-gray-500",
      textColor: "text-white",
    };
  }

  // --- Images ---
  if (
    lower.startsWith("image/") ||
    lower.includes("png") ||
    lower.includes("jpg") ||
    lower.includes("jpeg")
  ) {
    return {
      extension: "IMG",
      bgColor: "bg-cyan-500",
      textColor: "text-white",
      icon: Image,
    };
  }

  // --- Fallback ---
  return {
    extension: "FILE",
    bgColor: "bg-slate-500",
    textColor: "text-white",
    icon: File,
  };
};

interface SourceIconColoredProps {
  className?: string;
  iconClassName?: string;
}

export const getSourceIconColored: (
  mime: string,
  colored?: boolean
) => React.FC<SourceIconColoredProps> =
  (mime: string, colored: boolean = true) =>
  ({ className, iconClassName }) => {
    const config = getFileTypeConfig(mime);

    return (
      <div
        className={cn(
          "flex items-center justify-center rounded font-bold text-[9px] leading-none",
          "w-6 h-6",
          colored ? config.bgColor : "bg-slate-400/50",
          config.textColor,
          className
        )}
      >
        {config.icon ? (
          <config.icon className={cn("w-4 h-4", iconClassName)} />
        ) : (
          config.extension
        )}
      </div>
    );
  };

function getRelativeTimeString(date: string | Date): {
  text: string;
  isRecent: boolean;
} {
  const dateObj = new Date(date);
  const now = Date.now();
  const diff = now - dateObj.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  let text: string;
  let isRecent = false;

  if (seconds < 60) {
    text = "Just now";
    isRecent = true;
  } else if (minutes < 60) {
    text = `${minutes}min ago`;
    isRecent = true;
  } else if (hours < 24) {
    text = `${hours} hour${hours === 1 ? "" : "s"}  ago`;
    isRecent = true;
  } else if (days === 1) {
    text = "Yesterday";
    isRecent = true;
  } else if (days < 7) {
    text = `${days} days ago`;
    isRecent = true;
  } else if (weeks < 4) {
    text = `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  } else if (months < 12) {
    text = `${months} month${months === 1 ? "" : "s"}  ago`;
  } else {
    text = dateObj.toLocaleDateString();
  }

  return { text, isRecent };
}
/* -------------------------
   Props
------------------------- */

interface CourseSourcesTableProps {
  sources: StudyDocument[];
  files: StudyNotUploadedDocument[];
  selected: StudyNotUploadedDocument[];
  setSelected: React.Dispatch<React.SetStateAction<StudyNotUploadedDocument[]>>;

  allowedMimeTypes: string[];
  maxFileSizeBytes: number;
}

/* -------------------------
   Validation
------------------------- */

type FileValidationResult = {
  valid: boolean;
  reason?: "size" | "type";
};

function validateDocument(
  doc: StudyNotUploadedDocument,
  opts: {
    allowedMimeTypes: string[];
    maxFileSizeBytes: number;
  }
): FileValidationResult {
  const mime = doc.metadata?.content_type;

  if (
    mime &&
    opts.allowedMimeTypes.length > 0 &&
    !opts.allowedMimeTypes.includes(mime)
  ) {
    return { valid: false, reason: "type" };
  }

  const size = getFileSizeNumeric(doc);

  if (size != null && size > opts.maxFileSizeBytes) {
    return { valid: false, reason: "size" };
  }

  return { valid: true };
}

export const CONTENT_TYPE_PRIORITY: Record<string, number> = {
  // PowerPoint
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": 0, // PPTX
  "application/vnd.ms-powerpoint": 0, // PPT

  // PDF
  "application/pdf": 1,

  // Word
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 2, // DOCX
  "application/msword": 2, // DOC
};

/* -------------------------
   Component
------------------------- */

export function CourseSourcesTableB({
  sources,
  files,
  selected,
  setSelected,
  allowedMimeTypes,
  maxFileSizeBytes,
}: CourseSourcesTableProps) {
  const isLocked = (doc: StudyNotUploadedDocument) =>
    !!sources.find(
      (f) =>
        "canvas_file_id" in f.metadata &&
        f.metadata.canvas_file_id == doc.metadata.canvas_file_id
    );

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState("");

  const getValidation = (doc: StudyNotUploadedDocument) =>
    validateDocument(doc, {
      allowedMimeTypes,
      maxFileSizeBytes,
    });

  const isInvalid = (doc: StudyNotUploadedDocument) =>
    !getValidation(doc).valid;

  const isSelected = (doc: StudyNotUploadedDocument) =>
    !isInvalid(doc) && selected.some((s) => s == doc);

  const toggleRow = (doc: StudyNotUploadedDocument) => {
    if (isInvalid(doc) || isLocked(doc)) return;

    setSelected((prev) =>
      isSelected(doc) ? prev.filter((s) => s != doc) : [...prev, doc]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelected(files.filter((s) => !isInvalid(s) && !isLocked(s)));
    } else {
      setSelected([]);
    }
  };

  const columns = React.useMemo<ColumnDef<StudyNotUploadedDocument>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => {
          const rows = table.getRowModel().rows.map((r) => r.original);
          const selectableRows = rows.filter(
            (r) => !isInvalid(r) && !isLocked(r)
          );
          const allSelected =
            selectableRows.length > 0 &&
            selectableRows.every((r) => isSelected(r));

          return (
            <Checkbox
              checked={allSelected}
              onCheckedChange={(v) => toggleAll(!!v)}
              aria-label="Select all"
              className="border-2 ml-4"
            />
          );
        },
        cell: ({ row }) => {
          const doc = row.original;
          const invalid = isInvalid(doc);
          const locked = isLocked(doc);

          return (
            <Checkbox
              checked={isSelected(doc) || locked}
              disabled={invalid || locked}
              aria-label={`Select ${doc.name}`}
              className="border-2"
            />
          );
        },
        enableSorting: false,
      },
      {
        id: "name",
        accessorKey: "name",
        enableSorting: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="px-0 hover:bg-transparent font-semibold ml-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            File
            <ArrowUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
          </Button>
        ),
        cell: ({ row, getValue }) => {
          const validation = getValidation(row.original);
          const name = getValue<string>();
          const Icon = getSourceIconColored(
            "url" in row.original.metadata
              ? "url"
              : row.original.metadata["content_type"] || ""
          );

          return (
            <div className="flex items-center gap-4 min-w-0">
              <Icon className="flex-shrink-0" />
              <div className="min-w-0 flex-1">
                {validation.valid && (
                  <div
                    className={cn(
                      "truncate text-xs",
                      (validation.reason === "type" ||
                        validation.reason === "size") &&
                        "text-red-600 font-medium"
                    )}
                  >
                    {name}
                  </div>
                )}
                {validation.reason === "type" && (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle className="h-3 w-3" />
                    Unsupported file type: {name}
                  </div>
                )}
                {validation.reason === "size" && (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle className="h-3 w-3" />
                    File too large: {name}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },

      {
        id: "created",
        accessorKey: "metadata.created_at",
        enableSorting: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="px-0 hover:bg-transparent font-semibold ml-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Created
            <ArrowUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
          </Button>
        ),
        cell: ({ row }) => {
          const date = row.original.metadata.created_at;
          if (!date) return <span className="text-theme-2 text-xs">—</span>;

          const { text: relativeTime, isRecent: showNewBadge } =
            getRelativeTimeString(date);

          return (
            <div className="text-xs flex items-center gap-2">
              <div className="text-theme-1">{relativeTime}</div>
              {showNewBadge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 animate-pulse">
                  New
                </span>
              )}
            </div>
          );
        },
      },
    ],
    [selected, allowedMimeTypes, maxFileSizeBytes, sources]
  );

  const table = useReactTable({
    data: files.sort((a, b) => {
      const aInvalid = isInvalid(a) || isLocked(a);
      const bInvalid = isInvalid(b) || isLocked(b);

      // 1) Valid files first
      if (aInvalid && !bInvalid) return 1;
      if (!aInvalid && bInvalid) return -1;

      // 2) Content-type relevance (if present)
      const aType = a.metadata?.content_type;
      const bType = b.metadata?.content_type;

      const aPriority =
        aType && aType in CONTENT_TYPE_PRIORITY
          ? CONTENT_TYPE_PRIORITY[aType]
          : Number.MAX_SAFE_INTEGER;

      const bPriority =
        bType && bType in CONTENT_TYPE_PRIORITY
          ? CONTENT_TYPE_PRIORITY[bType]
          : Number.MAX_SAFE_INTEGER;

      if (aPriority !== bPriority) return aPriority - bPriority;

      // 3) Newer files first
      const aTime = new Date(a.metadata.created_at).valueOf();
      const bTime = new Date(b.metadata.created_at).valueOf();

      if (aTime !== bTime) return bTime - aTime;

      return 0;
    }),
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const validFileCount = files.filter(
    (f) => !isInvalid(f) && !isLocked(f)
  ).length;
  const selectedCount = selected.length;

  return (
    <div className="space-y-3">
      <div className="w-full">
        <Input
          placeholder="Search files..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          variant="search"
          className="w-full"
        />
        {/* <div className="text-sm text-theme-2 whitespace-nowrap">
          {selectedCount > 0 ? (
            <span className="font-medium text-theme-0">
              {selectedCount} selected
            </span>
          ) : (
            <span>{validFileCount} files available</span>
          )}
        </div> */}
      </div>

      <div className="rounded-lg border-2 border-theme bg-theme-0 max-h-[350px] overflow-hidden">
        <div className="overflow-y-auto max-h-[350px]">
          <table className="w-full table-fixed">
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b-2 border-theme">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(
                        header.column.id === "name" && "w-[60%]",
                        header.column.id === "select" && "w-12",
                        header.column.id === "created" && "w-40",
                        "bg-theme-1 text-theme-0 font-semibold h-11 text-left"
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    data-state={
                      isSelected(row.original) ? "selected" : undefined
                    }
                    onClick={() => toggleRow(row.original)}
                    className={cn(
                      "cursor-pointer transition-colors border-b border-theme",
                      "hover:bg-theme-1",
                      isSelected(row.original) &&
                        "bg-orange-400/30 hover:bg-orange-400/40 border-orange-400/40",
                      (isInvalid(row.original) || isLocked(row.original)) &&
                        "opacity-40 pointer-events-none bg-theme-1"
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={cn(
                          cell.column.id === "name" && "w-[60%]",
                          cell.column.id === "select" && "w-12",
                          cell.column.id === "created" && "w-40",
                          "py-2 px-4"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="h-32 text-center px-4"
                  >
                    <div className="flex flex-col items-center gap-2 text-theme-2">
                      <FileText className="h-8 w-8 opacity-30" />
                      <p className="text-sm">No files found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
